import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { downloadMediaUrl } from '../../../inlineMediaUrl';
import { openPublishDialog } from '../../../publish/publishDialogStore';
import { planDayDate } from '../rolloutDayCopy';
import { addDays } from '../planMode';
import { planMessages } from '../planMessages';
import { ChannelMark } from './ChannelMark';
import { MilestoneRow } from './MilestoneRow';
import { useRollout } from './RolloutContext';
import { rolloutMessages } from './rolloutMessages';
import type { MilestoneFacts } from './useMilestoneFacts';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Canvas step (design rollout-plan a1, b1, owner decision 2): a manual "I added it" mark, no
 * email, no reminder. "How?" and "I added it" open the manual panel of the publish dialog
 * (`spotifyCanvas`). With no Canvas loop the line sends the artist to the Kit.
 */
const CanvasMilestoneComponent: React.FC<{ facts: MilestoneFacts; isLast: boolean }> = ({ facts, isLast }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { accountId, item, plan, canAct } = useRollout();
  const { canvas, canvasDone } = facts;
  const day = plan.releaseDate === null ? '' : planDayDate(intl, addDays(plan.releaseDate, 1));

  const openMark = useCallback(() => {
    if (canvas === null) {
      return;
    }
    openPublishDialog({
      accountId,
      kitAssetUuid: canvas.uuid,
      catalogItemUuid: item.uuid,
      channel: 'spotifyCanvas',
      assetName: intl.formatMessage(rolloutMessages.canvasTitle),
      downloadUrl: canvas.fileUrl === null ? null : downloadMediaUrl(canvas.fileUrl),
    });
  }, [accountId, canvas, intl, item.uuid]);

  const openKit = useCallback(
    () => router.push(SMLRoutes.promoteRelease, { catalogItemUuid: item.uuid, tab: 'kit' }),
    [item.uuid, router]
  );

  return (
    <MilestoneRow kind={canvasDone ? 'done' : 'manual'} when="+1" day={day} isLast={isLast}>
      <span className={styles.msrKind}>{intl.formatMessage(rolloutMessages.youDoIt)}</span>
      <ChannelMark platform="spotify" small />
      {canvasDone ? (
        <span className={clsx(styles.pub, styles.pubDone)}>{intl.formatMessage(rolloutMessages.canvasDone)}</span>
      ) : (
        <>
          <b>{intl.formatMessage(rolloutMessages.canvasTitle)}</b>
          <span className={styles.msrSub}>
            {intl.formatMessage(canvas === null ? rolloutMessages.canvasNoLoop : rolloutMessages.canvasSub)}
          </span>
          <span className={styles.grow} />
          {canvas === null ? (
            <button type="button" className={styles.link} onClick={openKit}>
              {intl.formatMessage(rolloutMessages.canvasOpenKit)}
            </button>
          ) : (
            <>
              <button type="button" className={styles.link} onClick={openMark}>
                {intl.formatMessage(rolloutMessages.canvasHow)}
              </button>
              <button type="button" className={styles.link} disabled={!canAct} onClick={openMark}>
                {intl.formatMessage(rolloutMessages.canvasAdded)}
              </button>
            </>
          )}
        </>
      )}
    </MilestoneRow>
  );
};

export const CanvasMilestone = TypedMemo(CanvasMilestoneComponent);

/** "Forests is out" (design rollout-plan a1, b1): the release day line. */
const ReleaseDayMilestoneComponent: React.FC<{ isLast: boolean }> = ({ isLast }) => {
  const intl = useIntl();
  const { item, plan, today } = useRollout();
  const released = plan.releaseDate !== null && plan.releaseDate <= today;
  return (
    <MilestoneRow
      kind="releaseDay"
      when={intl.formatMessage(planMessages.dayZero)}
      day={plan.releaseDate === null ? '' : planDayDate(intl, plan.releaseDate)}
      isLast={isLast}
    >
      <span className={styles.msrKind}>{intl.formatMessage(rolloutMessages.milestone)}</span>
      <b>{intl.formatMessage(rolloutMessages.isOut, { title: item.title })}</b>
      {!released && <span className={styles.msrSub}>{intl.formatMessage(rolloutMessages.isOutSub)}</span>}
    </MilestoneRow>
  );
};

export const ReleaseDayMilestone = TypedMemo(ReleaseDayMilestoneComponent);
